import { Language } from '../../core/i18n/constants';
import { useLanguage } from '../../core/i18n/hooks/use-language';
import { SearchParam } from '../../core/routing/param';
import { BASE_BACKEND_QUERY_KEY, BASE_BACKEND_URL, QUERY_KEYS } from '../backend/constants';
import { QueryOptions, useAuthenticatedQuery } from '../backend/hooks/use-authenticated-query';
import { GetCustomerResponse, GetOpenOrdersResponse, GetRecentArticlesResponse } from '../backend/openapi/api-types';

const customerUrls = {
  getCustomer: () => `${BASE_BACKEND_URL}/customer` as const,
  getOpenOrders: () => `${BASE_BACKEND_URL}/customer/open-orders` as const,
  getRecentArticles: (requestedCount: number) => {
    const searchParams = new URLSearchParams();
    searchParams.append(SearchParam.count, requestedCount.toString());
    return `${BASE_BACKEND_URL}/customer/recent-articles?${searchParams.toString()}` as const;
  },
};

const customerQueryKeys = {
  customer: (language: Language) => [BASE_BACKEND_QUERY_KEY, language, QUERY_KEYS.customer],
  openOrders: (language: Language) => [BASE_BACKEND_QUERY_KEY, language, QUERY_KEYS.customer, QUERY_KEYS.openOrders],
  recentArticles: (language: Language, requestedCount: number) => [
    BASE_BACKEND_QUERY_KEY,
    language,
    QUERY_KEYS.customer,
    QUERY_KEYS.recentArticles,
    { requestedCount },
  ],
};

export const useCustomerQuery = () => {
  const language = useLanguage();
  const queryOptions: QueryOptions<GetCustomerResponse> = {
    options: {
      queryKey: customerQueryKeys.customer(language),
    },
    url: customerUrls.getCustomer(),
  };
  return useAuthenticatedQuery<GetCustomerResponse>(queryOptions);
};

export const useRecentArticlesQuery = (requestedCount: number) => {
  const language = useLanguage();
  const queryOptions: QueryOptions<GetRecentArticlesResponse> = {
    options: {
      queryKey: customerQueryKeys.recentArticles(language, requestedCount),
    },
    url: customerUrls.getRecentArticles(requestedCount),
  };
  return useAuthenticatedQuery<GetRecentArticlesResponse>(queryOptions);
};

export const useOpenOrdersQuery = () => {
  const language = useLanguage();
  const queryOptions: QueryOptions<GetOpenOrdersResponse> = {
    options: {
      queryKey: customerQueryKeys.openOrders(language),
    },
    url: customerUrls.getOpenOrders(),
  };
  return useAuthenticatedQuery<GetOpenOrdersResponse>(queryOptions);
};
